import React, { Fragment, useState, useEffect } from "react"
import Select from 'react-select'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
import { useJsApiLoader } from '@react-google-maps/api'
import TextareaAutosize from 'react-textarea-autosize'
import Cleave from 'cleave.js/react'
import 'cleave.js/dist/addons/cleave-phone.us' // eslint-disable-line

import { Input, Radio } from 'antd';

import Toggle from 'components/Toggle'
import SelectState from 'components/SelectState'
import SelectDate from 'components/SelectDate'
import { fetchLocation } from 'lib/utils'
import { checkValidation, validateQuestionAnswer } from 'lib/questionnaire-utils'

import styles from "./styles.module.scss"

const Question = props => {
  const [error, setError] = useState(false)
  const [isLoading, setIsLoading] = useState(null)
  const [info, setInfo] = useState(null)
  // const [answers, setAnswers] = useState(null)
  const [notApplicable, setNotApplicable] = useState(false)

  const { isLoaded: isGoogleLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_KEY,
    libraries: ['places','visualization']
  })

  useEffect(() => {
    setInfo({})
    setNotApplicable(false)
  }, [props.currentQuestionId])

  // Setters

  const _findComponent = (data, key, isShort = false) => {
    const isFound = data.find(itm => itm.types.indexOf(key) >= 0)
    if (isFound) {
      if (isShort) return isFound.short_name
      return isFound.long_name
    }
    return null
  }
  
  const _buildAddress = (data) => {
    const num = _findComponent(data, 'street_number')
    const street = _findComponent(data, 'route')
    let city = _findComponent(data, 'locality')
    if (!city) {
      city = _findComponent(data, 'sublocality')
    }
    const state = _findComponent(data, 'administrative_area_level_1', true)
    const zip = _findComponent(data, 'postal_code')
    return {
      address: `${num} ${street}`,
      city,
      state,
      zip
    }
  }

  const _onSetAddress = async a => {
    if (a?.value?.place_id) {
      const results = await geocodeByPlaceId(a.value.place_id)
      if (results.length > 0) {
        const { address_components } = results[0]
        const data = _buildAddress(address_components)
        setInfo({ ...info, address: { data, addressObject: a } })
      }
    }
  }

  const _setInfoByKey = (key, value) => {
    if (key === 'dob' || key === 'date') {
      setInfo({ ...info, ...value })
    } else {
      setInfo({ ...info, [key]: value })
    }
  }

  const _setZipCode = async (key, value) => {
    setInfo(i => { return { ...i, [key]: String(value) } })
    if (key === 'zip' && value.length === 5) {
      try {
        const location = await fetchLocation(value)
        if (!location.error && location.city && location.state) {
          setError(null)
          setInfo({ ...info, ...location })
        } else {
          setError('General error.')
        }
      } catch(e) {
        const error = (e.error.toLowerCase() === 'place not found.') ? 'Zip code is invalid, please try again.' : e.error
        setError(error)
      }
    }
  }

  // Helper

  const _getDropdown = (key, ops, placeholder=null) => {
    let options = []
    if (ops) {
      options = ops.map(itm => {
        return { value: itm, label: itm }
      })
    }

    let val = null
    if (info && info.text) {
      val = options.find(opt => opt.value === info.text)
    }
    
    return <Select
      id={key}
      autoFocus
      value={val}
      onChange={val =>  _setInfoByKey('text', val.value)}
      options={options}
      placeholder={placeholder}
      styles={{
        menu: provided => ({ ...provided, zIndex: 9999 })
      }}
    />
  }

  const _getAge = itm => {
    let options = Array.from(Array(88)).map((itm, key) => {
      const age = (12 + key)
      return { value: age, label: `${age} years old` }
    })

    let val = null
    if (info && info.age) {
      val = options.find(opt => opt.value === info.age)
    }
    
    return <Select
      id="age"
      value={val}
      onChange={val => _setInfoByKey('age', val.value)}
      options={options}
      placeholder="Select..."
      autoFocus
      styles={{
        menu: provided => ({ ...provided, zIndex: 9999 })
      }}
    />
  }

  const _getMonths = () => {
    let options = Array.from(Array(12)).map((itm, key) => {
      if (key === 1) {
        return { value: `${key}`, label: `${key} month` }
      }
      return { value: `${key}`, label: `${key} months` }
    })

    let val = null
    if (info && info.months) {
      val = options.find(opt => opt.value === info.months)
    }
    
    return <Select
      id="activeCounty"
      value={val}
      onChange={val =>  _setInfoByKey('months', val.value)}
      options={options}
      placeholder="How many Months?"
      styles={{
        menu: provided => ({ ...provided, zIndex: 9999 })
      }}
    />
  }

  const _getYears = () => {
    let options = Array.from(Array(99)).map((itm, key) => {
      if (key === 1) {
        return { value: `${key}`, label: `${key} year` }
      }
      return { value: `${key}`, label: `${key} years` }
    })

    let val = null
    if (info && info.years) {
      val = options.find(opt => opt.value === info.years)
    }
    
    return <Select
      id="activeCounty"
      value={val}
      onChange={val =>  _setInfoByKey('years', val.value)}
      options={options}
      placeholder="How many Years?"
      autoFocus
      styles={{
        menu: provided => ({ ...provided, zIndex: 9999 })
      }}
    />
  }

  const _getQuestion = () => {
    return props.section.questions.find(itm => itm.id === props.currentQuestionId)
  }

  const _doSubmit = async e => {
    e.preventDefault()

    const question = _getQuestion()
    if (question) {
      setError(null)
      setIsLoading('Saving!')
      try {
        const answer = validateQuestionAnswer(question, info, notApplicable)
        if (props.onSave) {
          await props.onSave(question.id, answer)
        }
        setTimeout(() => setIsLoading(null), 100)
      } catch(e) {
        setError(e.message)
        setIsLoading(null)
      }
    }
  }

  const _getInputByType = itm => {
    if (itm.type === 'AGE') {
      return (
        <div>
          {_getAge(itm)}
        </div>
      )
    } else if (itm.type === 'PHONE') {
      const defaultValue = (info && info.text) ? info.text : null
      return (
        <div className={styles.questionnaire__question__input__phone}>
          <Cleave 
            autoFocus
            value={defaultValue}
            placeholder="Enter phone number"
            options={{ blocks: [3,3,4], delimiter: '-', numericOnly: true }}
            onChange={e => _setInfoByKey('text', e.target.rawValue)} /> 
        </div>
      )
    } else if (itm.type === 'SSN') {
      const defaultValue = (info && info.text) ? info.text : null
      return (
        <div className={styles.questionnaire__question__input__ssn}>
          <Cleave 
            autoFocus
            value={defaultValue}
            placeholder="123-32-4312"
            options={{ blocks: [3,2,4], delimiter: '-', numericOnly: true }}
            onChange={e => _setInfoByKey('text', e.target.rawValue)} /> 
        </div>
      )
    } else if (itm.type === 'NAME') {
      const defaultNameFirst = (info && info.nameFirst) ? info.nameFirst : null
      const defaultNameLast = (info && info.nameLast) ? info.nameLast : null
      return (
        <div className={styles.questionnaire__question__input__name}>
          <Input autoFocus placeholder="First Name" defaultValue={defaultNameFirst} onChange={e => _setInfoByKey('nameFirst', e.target.value)} />
          <Input placeholder="Last Name" defaultValue={defaultNameLast} onChange={e => _setInfoByKey('nameLast', e.target.value)} />
        </div>
      )
    } else if (itm.type === 'FULLNAME') {
      const defaultNameFirst = (info && info.nameFirst) ? info.nameFirst : null
      const defaultNameMiddle = (info && info.nameMiddle) ? info.nameMiddle : null
      const defaultNameLast = (info && info.nameLast) ? info.nameLast : null
      return (
        <div className={styles.questionnaire__question__input__fullname}>
          <Input autoFocus placeholder="First Name" defaultValue={defaultNameFirst} onChange={e => _setInfoByKey('nameFirst', e.target.value)} />
          <Input placeholder="Middle Name" defaultValue={defaultNameMiddle} onChange={e => _setInfoByKey('nameMiddle', e.target.value)} />
          <Input placeholder="Last Name" defaultValue={defaultNameLast} onChange={e => _setInfoByKey('nameLast', e.target.value)} />

        {/* <input type="text" placeholder="First Name" defaultValue={defaultNameFirst} autoFocus onChange={e => _setInfoByKey('nameFirst', e.target.value)} />
        <input type="text" placeholder="Middle Name" defaultValue={defaultNameMiddle} onChange={e => _setInfoByKey('nameMiddle', e.target.value)} />
        <input type="text" placeholder="Last Name" defaultValue={defaultNameLast} onChange={e => _setInfoByKey('nameLast', e.target.value)} /> */}
        </div>
      )
    } else if (itm.type === 'LOCATION') {
      const city = info?.city ? info.city : ''
      const state = info?.state ? info.state : null 
      const country = info?.country ? info.country : null 
      let content = (
        <>
          <Input autoFocus placeholder="City Name" defaultValue={city} onChange={e => _setInfoByKey('city', e.target.value)} />
          <SelectState defaultState={state} onChange={val => _setInfoByKey('state', val.value)} />
        </>
      )
      if (info?.isForeignLocation) {
        content = (
          <>
            <input type="text" placeholder="Town" defaultValue={city} autoFocus onChange={e => _setInfoByKey('city', e.target.value)} />
            <input type="text" placeholder="Province" defaultValue={state} onChange={e => _setInfoByKey('state', e.target.value)} />
            <input type="text" placeholder="Country" defaultValue={country} onChange={e => _setInfoByKey('country', e.target.value)} />
          </>
        )
      }
      return (
        <div className={styles.questionnaire__question__input__location}>
          <div className={styles.questionnaire__question__input__location__inner}>
            {content}
          </div>
          <Toggle isYesNo className="foreignToggle" id="isForeignLocation" name="isForeignLocation" checked={info?.isForeignLocation || false} text="My location is outside the United States." onChange={(key, val) => _setInfoByKey(key, val)} />
        </div>
      )
    } else if (itm.type === 'LICENSE') {
      const number = (info && info.number) ? info.number : ''
      const state = (info && info.state) ? info.state : null
      return (
        <div className={styles.questionnaire__question__input__license}>
          <input type="text" placeholder="License Number" defaultValue={number} autoFocus onChange={e => _setInfoByKey('number', e.target.value)} />
          <SelectState defaultState={state} placeholder="License State"  onChange={val => _setInfoByKey('state', val.value)} />
        </div>
      )
    } else if (itm.type === 'STATE') {
      const state = (info && info.state) ? info.state : null
      return (
        <div className={styles.questionnaire__question__input__state}>
          <SelectState defaultState={state} autoFocus={true} placeholder={itm.placeHolder}  onChange={val => _setInfoByKey('state', val.value)} />
        </div>
      )
    } else if (itm.type === 'LENGTH_OF_TIME') {
      return (
        <div className={styles.questionnaire__question__input__lengthOfTime}>
          {_getYears()}
          {_getMonths()}
        </div>
      )
    } else if (itm.type === 'SELECT') {
      return (
        <div className={styles.questionnaire__question__input__select}>
          {_getDropdown(itm.slug, itm.metaData.options, itm.placeHolder)}
        </div>
      )
    } else if (itm.type === 'TEXTAREA') {
      const defaultValue = (info && info.text) ? info.text : null
      return (
        <div className={styles.questionnaire__question__input__textarea}>
          <TextareaAutosize defaultValue={defaultValue} autoFocus onChange={e => _setInfoByKey('text', e.target.value)} />
        </div>
      )
    } else if (itm.type === 'RADIO') {
      const defaultValue = (info && info.choice) ? info.choice : false
      return (
        <div className={styles.questionnaire__question__input__radio}>
          <Radio.Group onChange={e => _setInfoByKey('choice', e.target.value)} value={defaultValue}>
            {itm.options?.map((opt, optKey) => <Radio key={optKey} value={opt}>{opt}</Radio>)}
          </Radio.Group>
        </div>
      )
    } else if (itm.type === 'RADIO_YES_NO') {
      const defaultValue = (info && info.choice) ? info.choice : false
      return (
        <div className={styles.questionnaire__question__input__radio}>
          <Radio.Group onChange={e => _setInfoByKey('choice', e.target.value)} value={defaultValue}>
            <Radio value="YES">Yes</Radio>
            <Radio value="NO">No</Radio>
          </Radio.Group>
        </div>
      )
    } else if (itm.type === 'RADIO_YES_TEXT') {
      const defaultValue = (info && info.choice) ? info.choice : false
      const defaultTextValue = (info && info.choiceText) ? info.choiceText : null
      return (
        <div className={styles.questionnaire__question__input__radio}>
          <Radio.Group onChange={e => _setInfoByKey('choice', e.target.value)} value={defaultValue}>
            <Radio value="YES">Yes</Radio>
            <Radio value="NO">No</Radio>
          </Radio.Group>
          {(info && info.choice && info.choice === 'YES') && (
            <div className={styles.questionnaire__question__input__secondary}>
              <label>{itm.metaData.chosenOptionText}</label>
              <input type="text" defaultValue={defaultTextValue} autoFocus onChange={e => _setInfoByKey('choiceText', e.target.value)} />
            </div>
          )}
        </div>
      )
    } else if (itm.type === 'ADDRESS') {
      const defaultAddress = (info && info.address) ? info.address : null
      const defaultZip = (info && info.zip) ? info.zip : null
      return (
        <div className={styles.questionnaire__question__input__address}>
          <input type="text" placeholder="Street Address" defaultValue={defaultAddress} autoFocus onChange={e => _setInfoByKey('address', e.target.value)} />
          <input type="text" placeholder="Zip" defaultValue={defaultZip} onChange={e => {
            _setZipCode('zip', e.target.value)
          }} />
        </div>
      )
    } else if (itm.type === 'PLACES_ADDRESS') {
      const defaultAddress = (info?.address?.addressObject) ? info.address.addressObject : null
      return (
        <div className={styles.questionnaire__question__input__placesAddress}>
          {isGoogleLoaded && (
            <GooglePlacesAutocomplete
              selectProps={{
                autoFocus: true,
                value: defaultAddress,
                placeholder: "Start typing and we will search for your address...",
                styles: {
                  menu: provided => ({ ...provided, zIndex: 9999 })
                },
                onChange: _onSetAddress
              }}
            />
          )}
        </div>
      )
    } else if (['DOB'].indexOf(itm.type) >= 0) {
      const defaultDate = (info && info.month && info.day && info.year) ? `${info.year}-${info.month}-${info.day}` : null
      return (
        <div className={styles.questionnaire__question__input__date}>
          <SelectDate defaultDate={defaultDate} isDob={true} onFinal={(data) => _setInfoByKey('dob', data)} />
        </div>
      )
    } else if (['DATE'].indexOf(itm.type) >= 0) {
      const defaultDate = (info && info.month && info.day && info.year) ? `${info.year}-${info.month}-${info.day}` : null
      return (
        <div className={styles.questionnaire__question__input__date}>
          <SelectDate defaultDate={defaultDate} isDob={false} onFinal={(data) => _setInfoByKey('date', data)} />
        </div>
      )
    }
    const defaultValue = (info && info.text) ? info.text : null
    return (
      <div>
        <input type="text" defaultValue={defaultValue} autoFocus onChange={e => _setInfoByKey('text', e.target.value)} />
      </div>
    )
  }

  const _isNextDisabled = () => {
    const question = _getQuestion()
    if (question) {
      if (checkValidation(info, question.type) || notApplicable) {
        return false
      }
    }
    return true
  }

  const _developerBypass = async () => {
    await props.onDeveloperBypass()
  }

  const _getHeading = () => {
    if (props.section.area) {
      // if (props.section.area === 'INDEMNITOR') {
      //   return <h6>Indemnitor Information</h6>
      // }
      return <h6>Application Information</h6>
    }
    return null
  }

  const _getOrderSentence = (questionId) => {
    let currentNum = 0
    let num = 1
    props.section.questions.forEach(itm => {
      if (itm.id === questionId) {
        currentNum = num
      }
      num++
    })
    return <>Question {currentNum} of {props.section.questions.length}</>
  }

  const _displayQuestion = () => {
    const question = _getQuestion()
    if (question) {
      let helpText = question.helpText
      if (!question.isRequired && (!helpText || helpText === '')) {
        helpText = 'If you do not know, please choose Not Applicable'
      }

      return (
        <div className={`${styles.questionnaire__question} ${notApplicable ? styles.questionnaire__question__notApplicable : ''}`}>
          <header>
            {_getHeading()}
            <p className="big">{props.section.name}</p>
            {_getOrderSentence(question.id)}
          </header>
          <form onSubmit={_doSubmit}>
            <Fragment>
              {isLoading && <p className={styles.questionnaire__question__loading}>{isLoading}</p>}
              {!isLoading && (
                <div className={styles.questionnaire__question__input}>
                  <h5>{question.text.replace('employeed', 'employed')}</h5>
                  {_getInputByType(question)}
                  {helpText && <p className="helpText">{helpText}</p>}
                </div>
              )}
            </Fragment>
            {error && <p className={styles.questionnaire__question__error}>{error}</p>}
            <div className={styles.questionnaire__question__actions}>
              <div>
                <button type="submit" disabled={_isNextDisabled()} className="butt butt--teal">Next</button>
              </div>
            </div>
          </form>
          <div className={styles.questionnaire__question__na}>
            {!question.isRequired && (
            <button className={`${notApplicable ? 'butt butt--teal' : 'butt butt--grey'}`} onClick={e => {
              e.preventDefault()
              setNotApplicable(v => { return v ? false : true })
            }}>{`${notApplicable ? 'Applicable' : 'Not Applicable'}`}</button>)}
          </div>
        </div>
      )
    }
    return <p>Error</p>
  }

  return (
    <Fragment>
      {props.isTestCase && <button className="butt butt--red developerBypass" onClick={_developerBypass}>Developer Bypass</button>}
      {_displayQuestion()}
    </Fragment>
  )
}

export default Question